<template>
  <VRow class="pb-4">
    <VCol class="pb-0">
      <div class="d-flex justify-content-between flex-wrap">
        <div
          class="d-flex align-items-center overflow-x-auto"
          :class="{ 'header-mobile': $vuetify.breakpoint.xs }"
        >
          <div class="d-flex align-items-center">
            <span class="heading-5">{{ tripDate }}</span>
            <SBookingStatusLabel :bookingStatus="trip.bookingStatus" />
            <SPaymentStatusLabel
              v-if="isPaymentStatusVisible"
              :paymentType="trip.paymentType"
              :isFreeTrip="trip.bookingPrice === 0"
            />
          </div>
          <div class="d-flex ml-6 align-items-center">
            <VChip v-if="tripType && trip.haveShuttleTypeOptions" class="mr-2">
              {{ tripType }}
            </VChip>
            <VChip
              v-if="vehicleCategory && trip.haveVehicleOptions"
              class="mr-2"
            >
              {{ vehicleCategory }}
            </VChip>
          </div>
        </div>
        <VIcon color="textPrimary" />
        <VMenu v-if="canEditAndCancelBooking" bottom left>
          <template #activator="{ on, attrs }">
            <VBtn
              v-blur
              class="box-shadow-elevation-6 grey lighten-4"
              :data-test="`trip-${trip.bookingId}-dropdown`"
              icon
              :small="$vuetify.breakpoint.xs"
              v-bind="attrs"
              v-on="on"
            >
              <VIcon color="textPrimary"> mdi-dots-vertical </VIcon>
            </VBtn>
          </template>
          <VList>
            <VListItemGroup class="py-2">
              <VListItem class="body-1" @click="$emit('start-edit-comment')">
                <VIcon v-text="'mdi-message-reply-text'" />
                <span class="pl-4">
                  {{
                    $t(
                      doesCommentExist
                        ? "trips.editComment"
                        : "trips.addComment"
                    )
                  }}
                </span>
              </VListItem>
              <VListItem
                class="body-1"
                @click="$emit('open-cancellation-dialog')"
                :data-test="`trip-${trip.bookingId}-cancel`"
              >
                <VIcon color="error" class="mr-1" v-text="'mdi-cancel'" />
                <span class="pl-4">{{ $t("trips.cancelTrip") }}</span>
              </VListItem>
            </VListItemGroup>
          </VList>
        </VMenu>
      </div>
      <div class="mt-3">
        <span v-if="returnTrip" class="subtitle-2">{{
          `${$t("bookingForm.returnTrip")} - ${returnTrip}`
        }}</span>
      </div>
    </VCol>
  </VRow>
</template>

<script lang="ts">
export default {
  name: 'Header',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { getTranslateByStringMap } from '@slg/web-customer-shared/src/utils/translationHelper';
import SConstants from '@slg/web-customer-shared/src/utils/constants';
import STimeConverter from '@slg/web-customer-shared/src/utils/timeConverter';
import BookingStatusEnum from '@slg/web-customer-shared/src/enum/BookingStatusEnum';
import SBookingStatusLabel from '@slg/web-customer-shared/src/components/common/labels/SBookingStatusLabel.vue';
import SPaymentStatusLabel from '@slg/web-customer-shared/src/components/common/labels/SPaymentStatusLabel.vue';
import { useI18n, useRouteRef } from '../../../../composables';

const props = defineProps({
  trip: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const route = useRouteRef();

const tripType = computed(() => (props.trip.privateShuttle
  ? t('trips.private')
  : t('trips.shared')));

const featurePrice = computed(() => (props.trip?.selectedFeatures || []).reduce((acc: number, feature: { price: number; capacity: number }) => {
  acc += +feature.price * feature.capacity;
  return acc;
}, 0));

const ridePrice = computed(() => Number(props.trip.bookingPrice - featurePrice.value));

const vehicleCategory = computed(() => getTranslateByStringMap(props.trip.vehicleCategoryName));

const returnTrip = computed(() => (
  props.trip.returnTripDateTime &&
  STimeConverter.convertDateToFormat(
    props.trip.returnTripDateTime,
    props.trip.timeZone,
    SConstants.TIME_FORMAT.MMM_DD_HH_mm,
    route.value.params.lang
  )
));

const canEditAndCancelBooking = computed(() => (
  route.value.name !== 'Confirmation' &&
  (props.trip.bookingStatus === BookingStatusEnum.NEW ||
    props.trip.bookingStatus === BookingStatusEnum.ASSIGNED ||
    props.trip.bookingStatus === BookingStatusEnum.WAITING_ASSIGNMENT)
));

const doesCommentExist = computed(() => !!props.trip.comment?.length);

const isPaymentStatusVisible = computed(() => (
  props.trip.estimatedDropOffTime >= Date.now() &&
  props.trip.status !== BookingStatusEnum.CANCELLED
));

const tripDate = computed(() => STimeConverter.capitalizeFirstLetter(
  STimeConverter.convertDateToFormat(
    props.trip.dateTime,
    props.trip.timeZone,
    SConstants.TIME_FORMAT.MMMM_DD,
    route.value.params.lang
  )
));
</script>
<style lang="scss" scoped>
.header-mobile {
  width: 87%;
}
</style>
